import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { MediaList, PlayerControls, PlayerProgressBar } from '@app/shared/ui/molecules/Multimedia';
import { MediaData } from '@app/shared/types/player';
import { PlayerTitle } from '@app/shared/ui/atoms/Multimedia';
import { useAudioPlayer } from '@app/shared/hooks/useAudioPlayer';
import { getTrackUrl } from '@app/shared/utils/settlementDetails';
import { ActivityRewardModal } from '@app/shared/ui/organisms/Common';
import { Paragraph, Image, LoadingSpinner } from '@app/shared/ui/atoms/Common';
import { playerMeditationPanda, playerInsightsPanda } from '@app/assets';
import { cn } from '@app/shared/utils/common';

interface AudioPlayerProps {
  tracks: MediaData[];
  settlementId?: number;
}

export function AudioPlayer({ tracks, settlementId }: AudioPlayerProps) {
  const { t } = useTranslation();
  const {
    audioRef,
    currentTime,
    currentTrack,
    changeTrack,
    handleLoaded,
    handleTimeUpdate,
    togglePlay,
    duration,
    isPlaying,
    currentTrackIndex,
    isTrackFinished,
    setIsTrackFinished,
  } = useAudioPlayer(tracks, settlementId);

  if (!currentTrack) {
    return <LoadingSpinner />;
  }

  const audioSrc = useMemo(() => getTrackUrl(currentTrack.src), [currentTrack.src]);

  return (
    <div className="w-full mx-auto text-foreground select-none space-y-2 ">
      <Image
        src={currentTrack.type === 'meditation' ? playerMeditationPanda : playerInsightsPanda}
        className={cn(
          'absolute w-[130px] -top-[91%] left-1/2 -translate-x-1/2',
          currentTrack.type === 'meditation' && '-top-[97%]'
        )}
      />
      <div className="bg-white/15 rounded-3xl p-[12px_10px_4px] border border-white/30">
        <audio
          ref={audioRef}
          src={audioSrc}
          onLoadedMetadata={handleLoaded}
          onTimeUpdate={handleTimeUpdate}
        />

        <PlayerTitle title={currentTrack.title} description={currentTrack.description} />

        <PlayerProgressBar currentTime={currentTime} duration={duration} />

        <PlayerControls isPlaying={isPlaying} togglePlay={togglePlay} />
      </div>

      <MediaList data={tracks} currentIndex={currentTrackIndex} onDataItemClick={changeTrack} />

      <ActivityRewardModal
        isOpen={Boolean(isTrackFinished && settlementId)}
        setIsOpen={() => {
          setIsTrackFinished(false);
        }}
      >
        <Paragraph className="whitespace-pre-line">{t('reward_modal.title')}</Paragraph>
      </ActivityRewardModal>
    </div>
  );
}
